/* eslint-disable sort-keys */
/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import {
  useLocalization,
  useLocalizationContext,
} from '@im/react-localization';
import { NavItem, useNavTo } from '../../../nav';
import { AvatarPopover } from './AvatarPopover';
import ListItemIcon from '@mui/material/ListItemIcon';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Stack from '@mui/material/Stack';
import {
  useUserRole,
  useDisplayUserRoles,
} from 'src/provider/UserRoleProvider';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import { IM_PRICING_GDE_UI_SECURITYGROUP_ENABLED } from 'src/util/constants';
import { useSearchParams } from 'react-router-dom';

export type AvatarProps = {
  avatarImg?: string;
  'data-testid'?: string;
  firstName: string;
  lastName: string;
  navItems: NavItem[];
  onLogoutClick: () => void;
  resellerID: string;
  username: string;
};

export function Avatar(props: Readonly<AvatarProps>) {
  const {
    avatarImg,
    'data-testid': testid = 'required',
    firstName,
    lastName,
    navItems,
    onLogoutClick,
    resellerID,
    username,
  } = props;
  const theme = useTheme();
  const [openItems, setOpenItems] = useState<NavItem[]>([]);

  const showSubMenu = (navItem: NavItem, level: number, el: any) => {
    const newOpenItems = [...openItems];

    newOpenItems.length = level;
    if (navItem.navItems) {
      newOpenItems.push(navItem);
    }
    if (level === 0) setAnchorMenuEl(el);
    setOpenItems(newOpenItems);
    setTimeout(() => {
      setOpenItems([...newOpenItems]);
    }, 1);
  };

  const { translate } = useLocalization();
  const navTo = useNavTo();

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [anchorMenuEl, setAnchorMenuEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const isSecurityGroupEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_SECURITYGROUP_ENABLED
  );
  const [searchParams] = useSearchParams();
  const l10nContext = useLocalizationContext();
  const { locale } = l10nContext;

  const localeParts = locale.split('-');
  const countryCode =
    searchParams.get('countryCode') ??
    (localeParts.length > 1 ? localeParts[1] : '');

  const currentUserRole = useUserRole();
  const userRoles = useDisplayUserRoles();
  const userRolesMap = useMemo(
    () => new Map(userRoles.map((r) => [r.value, r.label])),
    [userRoles]
  );

  const displayRoles = useMemo(() => {
    let roles: string[] = [];

    if (isSecurityGroupEnabled) {
      const rolesForCountry = currentUserRole.listOfAccessableCountries.filter(
        (role) =>
          countryCode &&
          role.siteId?.toLowerCase() === countryCode.toLowerCase() &&
          role.roleId !== 120
      );

      const uniqueRoleIds = Array.from(
        new Set(rolesForCountry.map((role) => role.roleId))
      );

      roles = roles = uniqueRoleIds
        .map((roleId) => {
          if (roleId === 110) return 'COG';
          return userRolesMap.get(roleId) ?? null;
        })
        .filter(Boolean) as string[];
    } else {
      roles = currentUserRole.role
        .filter((roleValue: number) => roleValue !== 120)
        .map((roleValue) => {
          if (roleValue === 110) return 'COG';
          return userRolesMap.get(roleValue) ?? null;
        })
        .filter(Boolean) as string[];
    }
    return roles;
  }, [isSecurityGroupEnabled, countryCode, currentUserRole, userRolesMap]);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    setOpenItems([]);
    setAnchorMenuEl(null);
    setAnchorEl(null);
  };

  const getInitials = (firstName = '', lastName = '') => {
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
  };

  return (
    <>
      <IconButton
        aria-controls={open ? 'account-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        data-testid={`${testid}-avatarBtn`}
        onClick={(e) => setAnchorEl(e.target)}
        sx={{ padding: '0px' }}
      >
        {avatarImg ? (
          <img
            alt={`${firstName} ${lastName} avatar`}
            data-testid={`${testid}-avatarImg`}
            height="32px"
            src={avatarImg}
            width="32px"
          />
        ) : (
          <InitialsCircle>
            <Typography
              data-testid={`${testid}-avatarInitials`}
              variant="bodycopyregular"
            >
              {getInitials(firstName, lastName)}
            </Typography>
          </InitialsCircle>
        )}
      </IconButton>

      <Menu
        PaperProps={paperProps}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="account-menu"
        marginThreshold={10}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Stack direction="column">
          <Typography variant="smalltextsemibold">{resellerID}</Typography>
        </Stack>
        <Stack direction="column">
          <Typography sx={{ pt: '8px' }} variant="smalltextregular">
            {translate('myprofile.UserName')}
          </Typography>
          <Typography variant="smalltextsemibold">{username}</Typography>
        </Stack>

        <Stack direction="column" sx={{ mt: 2 }}>
          <Typography variant="smalltextregular">
            {translate('UserRoles.UserRole')}
          </Typography>
          {displayRoles.length > 0 ? (
            displayRoles.map((label, idx) => (
              <Typography
                key={typeof label === 'string' ? label : idx}
                sx={{
                  display: 'flex',
                  fontWeight: 700,
                }}
                variant="smalltextregular"
              >
                {label}
              </Typography>
            ))
          ) : (
            <Typography variant="smalltextregular">
              {translate('UserRoles.NoUserRole')}
            </Typography>
          )}
        </Stack>

        {navItems
          ? navItems.map((navItem) => (
              <MenuItem
                key={navItem.key}
                onClick={(e) => {
                  if (!navItem.navItems) {
                    handleClose();
                    navTo(navItem);
                  } else showSubMenu(navItem, 0, e.target);
                }}
                sx={{ minHeight: '32px !important', padding: '8px 12px' }}
              >
                {navItem.navItems && (
                  <ListItemIcon style={{ minWidth: '15px' }}>
                    <ArrowBackIosIcon sx={{ height: '9px', width: '9px' }} />
                  </ListItemIcon>
                )}
                <Typography variant="bodycopyregular">
                  <ListItemText
                    data-testid={`${navItem.key}-MenuItem`}
                    primary={navItem.label ?? translate(navItem.key)}
                  />
                </Typography>
              </MenuItem>
            ))
          : null}

        <AvatarPopover
          anchorEl={anchorMenuEl}
          closePopover={handlePopoverClose}
          open={Boolean(openItems.length)}
          openItems={openItems}
          showSubMenu={showSubMenu}
        />

        <StyledDivider />
        <Box>
          <Button
            aria-controls={open ? 'demo-customized-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            data-testid="logout"
            disableElevation
            onClick={() => onLogoutClick()}
            sx={{
              backgroundColor: theme.palette.primary[700],
              borderColor: theme.palette.primary[700],
              color: theme.palette.common.white,
              display: 'block',
              mx: 'auto',
              mt: '10px',
            }}
            variant="contained"
          >
            <Typography variant="bodycopyregular">
              {translate('Navigation.LogOut')}
            </Typography>
          </Button>
        </Box>
      </Menu>
    </>
  );
}
const InitialsCircle = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.primary[700],
  borderRadius: '100%',
  color: theme.palette.common.white,
  display: 'flex',
  height: '32px',
  justifyContent: 'center',
  width: '32px',
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderWidth: '0',
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  marginTop: '8px',
}));

const paperProps = {
  elevation: 0,
  sx: {
    '& .MuiAvatar-root': {
      height: 90,
      ml: -0.5,
      mr: 1,
      width: 32,
    },
    '& .MuiMenu-list': {
      minWidth: 156,
      padding: 0,
    },
    '& .MuiMenuItem-root .MuiListItemText-root .MuiTypography-root': {
      color: '#333333',
      fontSize: 14,
      lineHeight: '22px',
      my: '-4px',
    },
    '&:before': {
      bgcolor: 'background.paper',
      content: '""',
      display: 'block',
      height: 18,
      position: 'absolute',
      right: 14,
      top: 2,
      transform: 'translateY(-60%) rotate(45deg)',
      width: 18,
      zIndex: 0,
    },
    borderRadius: '4px',
    filter: 'drop-shadow(0px 2px 6px rgba(51, 51, 51, 0.15))',
    mt: 1.5,
    overflow: 'visible',
    padding: '16px',
  },
};
